import { Box } from '@mui/material';
import React from 'react';
import { KitCard, KitList, KitPill } from '../common/MobileKit';
import { KitBanner } from '../common/MobileKitExtras';
import { MobileListRow } from '../common/MobileCrud';
import { protoIcon } from '../common/ProtoIcon';

/**
 * Peças do Relatório de Fluxo de Caixa no shell mobile (tabela mensal em linhas e avisos).
 * Só apresentação: os dados e a formatação vêm de `pages/reports/CashFlowReport.js`.
 */

const AlertIcon = protoIcon('alert');
const InfoIcon = protoIcon('info');

const lines = (a, b) => (
    <>
        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{a}</Box>
        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{b}</Box>
    </>
);

const netPill = (net, formatBrl) => (
    <KitPill tone={Number(net) >= 0 ? 'success' : 'danger'}>{`${Number(net) >= 0 ? '+' : ''}${formatBrl(net)}`}</KitPill>
);

/**
 * "Tabela mensal": Saldo inicial, uma linha por mês (Entradas, Saídas, Líquido, Saldo) e Total do período.
 * - formatBrl(value), monthLabel(row): os mesmos helpers do desktop
 */
export function MonthlyCashList({ data, formatBrl, monthLabel }) {
    const startLabel = data.start_date ? new Date(`${String(data.start_date).slice(0, 10)}T00:00:00`).toLocaleDateString('pt-BR') : '';
    return (
        <KitList>
            <MobileListRow
                title={<Box component="span" sx={{ fontStyle: 'italic', color: 'text.disabled', fontWeight: 600 }}>{`Saldo inicial (${startLabel})`}</Box>}
                end={<Box sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.4 }}>{formatBrl(data.opening_balance)}</Box>}
            />
            {data.period.map((row) => (
                <MobileListRow
                    key={`${row.year}-${row.month}`}
                    title={monthLabel(row)}
                    sub={lines(`Entradas ${formatBrl(row.inflows)}`, `Saídas ${formatBrl(row.outflows)}`)}
                    end={(
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                            {netPill(row.net, formatBrl)}
                            <Box sx={{ fontWeight: 800, fontSize: 13, lineHeight: 1.4 }}>{`Saldo ${formatBrl(row.closing_balance)}`}</Box>
                        </Box>
                    )}
                />
            ))}
            <MobileListRow
                sx={{ backgroundColor: 'background.subtle' }}
                title={<Box component="span" sx={{ fontWeight: 800 }}>Total do período</Box>}
                sub={lines(`Entradas ${formatBrl(data.total_inflows)}`, `Saídas ${formatBrl(data.total_outflows)}`)}
                end={(
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                        {netPill(data.net_period, formatBrl)}
                        <Box sx={{ fontWeight: 800, fontSize: 13, lineHeight: 1.4 }}>{`Saldo ${formatBrl(data.closing_balance)}`}</Box>
                    </Box>
                )}
            />
        </KitList>
    );
}

// Avisos da aba "Tabela mensal" (mesmos textos do desktop).
export function CashFlowNotices({ negativeMessage, periodIsEmpty }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', mb: '14px' }}>
            {periodIsEmpty && (
                <KitBanner tone="info" icon={InfoIcon}>Nenhuma movimentação no período (entradas e saídas zeradas nos meses exibidos).</KitBanner>
            )}
            {negativeMessage && <KitBanner icon={AlertIcon}>{negativeMessage}</KitBanner>}
            <KitBanner tone="info" icon={InfoIcon}>
                As <b>saídas</b> consideram apenas despesas com status <b>Prestado Contas</b>, posicionadas pela <b>data de pagamento</b> (ou data do lançamento quando aplicável). Despesas <b>Pendente</b> não entram nas saídas consolidadas. O <b>saldo</b> é uma posição acumulada a partir das transações cadastradas e não necessariamente corresponde ao extrato bancário ou ao caixa registradora.
            </KitBanner>
            <KitCard sx={{ fontSize: 11.5, color: 'text.disabled', lineHeight: 1.4 }}>
                Valores consolidados por mês. A linha <b>Saldo inicial</b> representa o saldo em caixa no início do período filtrado; a linha <b>Total do período</b> soma entradas e saídas de todos os meses.
            </KitCard>
        </Box>
    );
}
